import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  TrendingUp, TrendingDown, RefreshCw, Plus, Calendar, DollarSign,
  ArrowRightLeft, History, Globe
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../../../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "../../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../../components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Cotizaciones = () => {
  const [loading, setLoading] = useState(true);
  const [monedas, setMonedas] = useState([]);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [ultimasCotizaciones, setUltimasCotizaciones] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filtroMoneda, setFiltroMoneda] = useState("");

  const [form, setForm] = useState({
    fecha: new Date().toISOString().split('T')[0],
    moneda_id: "",
    cotizacion_compra: "",
    cotizacion_venta: "",
    cotizacion_oficial: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [monedasRes, cotizacionesRes] = await Promise.all([
        axios.get(API_URL + "/api/erp/monedas"),
        axios.get(API_URL + "/api/erp/cotizaciones")
      ]);
      
      // Filtrar monedas que no son ARS (la moneda base)
      const monedasExtranjeras = monedasRes.data.filter(m => !m.es_default);
      setMonedas(monedasExtranjeras);
      setCotizaciones(cotizacionesRes.data);

      // Obtener última cotización de cada moneda
      const ultimas = {};
      for (const moneda of monedasExtranjeras) {
        try {
          const res = await axios.get(API_URL + "/api/erp/cotizaciones/ultima/" + moneda.id);
          if (res.data) {
            ultimas[moneda.id] = res.data;
          }
        } catch (e) {
          // No hay cotización para esta moneda
        }
      }
      setUltimasCotizaciones(ultimas);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setForm({
      fecha: new Date().toISOString().split('T')[0],
      moneda_id: monedas.length > 0 ? monedas[0].id.toString() : "",
      cotizacion_compra: "",
      cotizacion_venta: "",
      cotizacion_oficial: ""
    });
    setModalOpen(true);
  };

  const handleGuardar = async () => {
    if (!form.moneda_id || !form.cotizacion_compra || !form.cotizacion_venta) {
      toast.error("Complete los campos requeridos");
      return;
    }

    setSaving(true);
    try {
      await axios.post(API_URL + "/api/erp/cotizaciones", {
        fecha: form.fecha,
        moneda_id: parseInt(form.moneda_id),
        cotizacion_compra: parseFloat(form.cotizacion_compra),
        cotizacion_venta: parseFloat(form.cotizacion_venta),
        cotizacion_oficial: form.cotizacion_oficial ? parseFloat(form.cotizacion_oficial) : null
      });
      toast.success("Cotización registrada correctamente");
      setModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar cotización");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(value);
  };

  const cotizacionesFiltradas = filtroMoneda 
    ? cotizaciones.filter(c => c.moneda_codigo === filtroMoneda)
    : cotizaciones;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2853B9]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cotizaciones">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3876]">Cotizaciones</h1>
          <p className="text-gray-600">Registro de tipos de cambio diarios</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={fetchData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={handleOpenModal} className="bg-[#2853B9] hover:bg-[#1E3876]">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Cotización
          </Button>
        </div>
      </div>

      {/* Cotizaciones actuales */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {monedas.map((moneda) => {
          const ultima = ultimasCotizaciones[moneda.id];
          return (
            <Card key={moneda.id} className="cao-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <span className="font-bold text-blue-600">{moneda.simbolo}</span>
                    </div>
                    <div>
                      <CardTitle className="text-lg">{moneda.codigo}</CardTitle>
                      <CardDescription>{moneda.nombre}</CardDescription>
                    </div>
                  </div>
                  <Globe className="w-5 h-5 text-gray-400" />
                </div>
              </CardHeader>
              <CardContent>
                {ultima ? (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Fecha:</span>
                      <Badge variant="outline">{ultima.fecha}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-xs text-green-600 mb-1">Compra</p>
                        <p className="text-xl font-bold text-green-700">$ {formatCurrency(ultima.cotizacion_compra)}</p>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg">
                        <p className="text-xs text-red-600 mb-1">Venta</p>
                        <p className="text-xl font-bold text-red-700">$ {formatCurrency(ultima.cotizacion_venta)}</p>
                      </div>
                    </div>
                    {ultima.cotizacion_oficial && (
                      <div className="p-2 bg-gray-50 rounded-lg text-center">
                        <p className="text-xs text-gray-500">Oficial: $ {formatCurrency(ultima.cotizacion_oficial)}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <p className="text-sm">Sin cotización registrada</p>
                    <Button 
                      variant="link" 
                      size="sm" 
                      onClick={() => {
                        setForm({...form, moneda_id: moneda.id.toString()});
                        setModalOpen(true);
                      }}
                    >
                      Agregar primera cotización
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        {monedas.length === 0 && (
          <Card className="cao-card col-span-3">
            <CardContent className="p-8 text-center text-gray-500">
              No hay monedas extranjeras configuradas.
            </CardContent>
          </Card>
        )}
      </div>

      {/* Calculadora de conversión */}
      <Card className="cao-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5" />
            Conversor Rápido
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ConversorMoneda monedas={monedas} ultimasCotizaciones={ultimasCotizaciones} />
        </CardContent>
      </Card>

      {/* Historial */}
      <Card className="cao-card">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Historial de Cotizaciones
            </CardTitle>
            <Select value={filtroMoneda} onValueChange={setFiltroMoneda}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TODAS">Todas</SelectItem>
                {monedas.map((m) => (
                  <SelectItem key={m.id} value={m.codigo}>{m.codigo}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Moneda</TableHead>
                <TableHead className="text-right">Compra</TableHead>
                <TableHead className="text-right">Venta</TableHead>
                <TableHead className="text-right">Oficial</TableHead>
                <TableHead className="text-right">Spread</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cotizacionesFiltradas.map((cot) => {
                const spread = ((cot.cotizacion_venta - cot.cotizacion_compra) / cot.cotizacion_compra * 100).toFixed(2);
                return (
                  <TableRow key={cot.id}>
                    <TableCell>{cot.fecha}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{cot.moneda_codigo}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-green-600">
                      $ {formatCurrency(cot.cotizacion_compra)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-red-600">
                      $ {formatCurrency(cot.cotizacion_venta)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {cot.cotizacion_oficial ? "$ " + formatCurrency(cot.cotizacion_oficial) : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge className="bg-gray-100 text-gray-700">{spread}%</Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
              {cotizacionesFiltradas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No hay cotizaciones registradas
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal Nueva Cotización */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Nueva Cotización</DialogTitle>
            <DialogDescription>
              Registre el tipo de cambio del día
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Input 
                  type="date" 
                  value={form.fecha} 
                  onChange={(e) => setForm({...form, fecha: e.target.value})} 
                />
              </div>
              <div className="space-y-2">
                <Label>Moneda *</Label>
                <Select value={form.moneda_id} onValueChange={(v) => setForm({...form, moneda_id: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {monedas.map((m) => (
                      <SelectItem key={m.id} value={m.id.toString()}>
                        {m.simbolo} {m.codigo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-500" />
                  Compra *
                </Label>
                <Input 
                  type="number" 
                  step="0.01"
                  placeholder="0.00"
                  value={form.cotizacion_compra} 
                  onChange={(e) => setForm({...form, cotizacion_compra: e.target.value})} 
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-red-500" />
                  Venta *
                </Label>
                <Input 
                  type="number" 
                  step="0.01"
                  placeholder="0.00"
                  value={form.cotizacion_venta} 
                  onChange={(e) => setForm({...form, cotizacion_venta: e.target.value})} 
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Cotización Oficial (opcional)</Label>
              <Input 
                type="number" 
                step="0.01"
                placeholder="0.00"
                value={form.cotizacion_oficial} 
                onChange={(e) => setForm({...form, cotizacion_oficial: e.target.value})} 
              />
              <p className="text-xs text-gray-500">Tipo de cambio oficial del BCRA</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button 
              onClick={handleGuardar} 
              disabled={saving}
              className="bg-[#2853B9] hover:bg-[#1E3876]"
            >
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Componente Conversor de Monedas
const ConversorMoneda = ({ monedas, ultimasCotizaciones }) => {
  const [monto, setMonto] = useState("");
  const [monedaOrigen, setMonedaOrigen] = useState("ARS");
  const [monedaDestino, setMonedaDestino] = useState("");

  useEffect(() => {
    if (monedas.length > 0 && !monedaDestino) {
      setMonedaDestino(monedas[0].codigo);
    }
  }, [monedas, monedaDestino]);

  const calcularConversion = () => {
    if (!monto || !monedaDestino) return null;

    const monedaInfo = monedas.find(m => m.codigo === monedaDestino);
    if (!monedaInfo) return null;

    const cotizacion = ultimasCotizaciones[monedaInfo.id];
    if (!cotizacion) return null;

    const montoNum = parseFloat(monto);

    if (monedaOrigen === "ARS") {
      // ARS -> Extranjera (dividir por venta)
      return {
        resultado: montoNum / cotizacion.cotizacion_venta,
        cotizacion: cotizacion.cotizacion_venta,
        tipo: "venta"
      };
    } else {
      // Extranjera -> ARS (multiplicar por compra)
      return {
        resultado: montoNum * cotizacion.cotizacion_compra,
        cotizacion: cotizacion.cotizacion_compra,
        tipo: "compra"
      };
    }
  };

  const intercambiar = () => {
    if (monedaOrigen === "ARS") {
      setMonedaOrigen(monedaDestino);
      setMonedaDestino("ARS");
    } else {
      setMonedaDestino(monedaOrigen);
      setMonedaOrigen("ARS");
    }
  };

  const conversion = calcularConversion();

  return (
    <div className="flex flex-col sm:flex-row items-center gap-4">
      <div className="flex-1 w-full">
        <Label className="text-xs text-gray-500">Monto</Label>
        <div className="flex gap-2">
          <Input
            type="number"
            placeholder="0.00"
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
            className="flex-1"
          />
          <Select value={monedaOrigen} onValueChange={setMonedaOrigen}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ARS">ARS</SelectItem>
              {monedas.map((m) => (
                <SelectItem key={m.id} value={m.codigo}>{m.codigo}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button variant="outline" size="icon" onClick={intercambiar} className="shrink-0">
        <ArrowRightLeft className="w-4 h-4" />
      </Button>

      <div className="flex-1 w-full">
        <Label className="text-xs text-gray-500">Resultado</Label>
        <div className="flex gap-2">
          <div className="flex-1 p-2 bg-gray-100 rounded-md font-mono text-lg">
            {conversion ? conversion.resultado.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00"}
          </div>
          <Select value={monedaDestino} onValueChange={setMonedaDestino}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ARS">ARS</SelectItem>
              {monedas.map((m) => (
                <SelectItem key={m.id} value={m.codigo}>{m.codigo}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {conversion && (
          <p className="text-xs text-gray-500 mt-1">
            TC {conversion.tipo}: $ {conversion.cotizacion.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
          </p>
        )}
      </div>
    </div>
  );
};

export default Cotizaciones;
